import React, { useState, useEffect, Fragment } from 'react'
import {
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  MenuItem,
  Box,
  Typography
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { ProductOriginsDDL } from '../../../components/Producto/ProductOrigins/ProductOriginsDDL'
import { useApi } from '../../../hooks/useApi'
import {
  getVasRuleByBrand,
  getVasRuleList,
  getVasRuleTemplate,
  postVasRuleMassive
} from './../../ProductAdmin/config/api'
import { VasRule } from '../../../interfaces/VasRule/VasRule'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { ActyonMode } from '../types'
import { useProductOrigins } from '../../../hooks'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'

interface VasRuleMasiveModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
}

export const VasRuleMasiveModal: React.FC<VasRuleMasiveModalProps> = ({
  handleSnackbar,
  handleCloseModal
}) => {
  const [productOriginId, setProductOriginId] = useState<number>(0)
  const [operationType, setOperationType] = useState<ActyonMode>('ADD')
  const [vasRuleId, setVasRuleId] = useState<number>(0)
  const [selectedVasName, setSelectedVasName] = useState<string>('')
  const [selectedOriginName, seSelectedOriginName] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [fileName, setFileName] = useState<string>(
    'Ningún archivo seleccionado'
  )

  // Estado para cargar reglas VAS
  const { state: vasRulesState, callApi: callVasRules } =
    useApi<ApiResponse<VasRule>>()

  // Estado para descargar template
  const { state: templateState, callApi: callDownloadTemplate } = useApi()
  const { state: createMassiveState, callApi: callCreateMassive } = useApi()

  // Cargar lista de reglas VAS cuando cambia el tipo de operación o la marca
  useEffect(() => {
    // Resetear la selección de regla VAS cuando cambien marca u operación
    setVasRuleId(0)

    if (productOriginId === 0) {
      return // No cargar si no hay marca seleccionada
    }

    if (operationType === 'ADD') {
      // Para asociar, cargar todas las reglas VAS disponibles
      callVasRules(getVasRuleList([], 0, 250))
    } else {
      // Para eliminar, cargar reglas VAS por marca
      callVasRules(getVasRuleByBrand(productOriginId, 0, 250))
    }
  }, [operationType, productOriginId])

  const {
    getProductOrigins,
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  useEffect(() => {
    // Cargar marcas al inicializar la página
    getProductOrigins()
  }, [])

  const handleSelectFile = (event: any) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setSelectedFile(files[0])
      setFileName(files[0].name)
    }
  }

  const handleDownloadTemplate = () => {
    callDownloadTemplate(getVasRuleTemplate())
      .then((response: any) => {
        const raw = window.atob(response.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], { type: response.mimeType })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch((error) => {
        handleSnackbar('Error al descargar el template: ' + error, false)
      })
  }

  const handleSave = () => {
    // Validaciones
    if (productOriginId === 0) {
      handleSnackbar('Debe seleccionar una marca', false)
      return
    }
    if (vasRuleId === 0) {
      handleSnackbar('Debe seleccionar una regla VAS', false)
      return
    }
    if (!selectedFile) {
      handleSnackbar('Debe seleccionar un archivo', false)
      return
    }

    const formData = new FormData()

    // Agregar los valores del formulario
    formData.append('productOriginId', productOriginId.toString())
    formData.append('isDeleted', operationType === 'REMOVE' ? 'true' : 'false')
    formData.append('vasRuleId', vasRuleId.toString())

    // Agregar el archivo seleccionado
    formData.append('file', selectedFile)

    callCreateMassive(postVasRuleMassive(formData))
      .then(() => {
        handleSnackbar('Carga realizada exitosamente', true)
        handleCloseModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  useEffect(() => {
    if (productOriginId && productOrigins) {
      const selected = productOrigins.find(
        (v) => v.productOriginId === productOriginId
      )
      seSelectedOriginName(selected?.name || '')
    } else {
      seSelectedOriginName('')
    }
  }, [productOriginId])

  useEffect(() => {
    if (vasRuleId && vasRulesState.data?.items) {
      const selected = vasRulesState.data?.items.find(
        (v) => v.vasRuleId === vasRuleId
      )

      setSelectedVasName(selected?.name || '')
    } else {
      setSelectedVasName('')
    }
  }, [vasRuleId])

  const isDisabled = productOriginId === 0 || vasRuleId === 0 || !selectedFile

  const vasRules = vasRulesState.data?.items || []
  const confirmModal = useModal('')

  if (templateState.status === Status.Loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      {/* Descripción */}
      <Typography variant='body2' color='textSecondary'>
        Configura los parámetros para{' '}
        <strong>asociar un VAS a productos de una marca</strong>.
      </Typography>

      {/* Marca */}
      <FormControl fullWidth>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
          Marca *
        </Typography>
        <ProductOriginsDDL
          onChange={(e: any) => setProductOriginId(e.target.value as number)}
          value={productOriginId}
          type='outlined'
          fullWidth
          error={productOriginId === 0}
        />
      </FormControl>

      {/* Tipo de operación */}
      <FormControl fullWidth>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
          Tipo de operación *
        </Typography>
        <RadioGroup
          row
          value={operationType}
          onChange={(e) => setOperationType(e.target.value as 'ADD' | 'REMOVE')}
        >
          <FormControlLabel
            value='ADD'
            control={<Radio />}
            label='Asociar VAS'
          />
          <FormControlLabel
            value='REMOVE'
            control={<Radio />}
            label='Eliminar VAS'
          />
        </RadioGroup>
        <Typography variant='caption' color='textSecondary' sx={{ mt: 0.5 }}>
          Solo puedes seleccionar un VAS.
        </Typography>
      </FormControl>

      {/* Seleccionar Regla VAS */}
      <FormControl fullWidth disabled={productOriginId === 0}>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
          Seleccionar Regla VAS *
        </Typography>
        <TextField
          select
          value={vasRuleId}
          onChange={(e) => setVasRuleId(e.target.value as any)}
          variant='outlined'
          error={vasRuleId === 0}
          disabled={productOriginId === 0}
        >
          <MenuItem value={0} disabled>
            {vasRulesState.status === Status.Loading
              ? 'Cargando...'
              : 'Seleccionar regla'}
          </MenuItem>
          {vasRules.map((rule) => (
            <MenuItem key={rule.vasRuleId} value={rule.vasRuleId}>
              {rule.name}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant='caption' color='textSecondary' sx={{ mt: 0.5 }}>
          Solo puedes seleccionar un VAS.
        </Typography>
      </FormControl>

      {/* Cargar archivo */}
      <FormControl fullWidth>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
          Cargar archivo de SKUs
        </Typography>
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <label htmlFor='file-upload'>
              <input
                id='file-upload'
                type='file'
                onChange={handleSelectFile}
                accept='.xls,.xlsx,.csv'
                style={{ display: 'none' }}
              />
              <Button variant='outlined' component='span' size='small'>
                Seleccionar archivo
              </Button>
            </label>
            <Typography variant='caption'>{fileName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              onClick={(e) => {
                e.preventDefault()
                handleDownloadTemplate()
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': { opacity: 0.7 }
              }}
            >
              <FileDownloadIcon
                fontSize='small'
                sx={{ color: 'primary.main' }}
              />
              <Typography variant='caption' sx={{ color: 'primary.main' }}>
                Descargar Template
              </Typography>
            </Box>
          </Box>
        </Box>
      </FormControl>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant='outlined'>
          Cancelar
        </Button>
        <Button
          onClick={() =>
            confirmModal.openModal(
              'sm',
              undefined,
              `${operationType === 'REMOVE' ? 'Eliminar' : 'Asociar'}  Regla VAS`
            )
          }
          variant='contained'
          color='primary'
          disabled={isDisabled}
        >
          Guardar
        </Button>
      </DialogActions>
      <Modal
        open={confirmModal.isOpen}
        maxWidth={confirmModal.size}
        onClose={confirmModal.closeModal}
        title={confirmModal.title}
        loading={createMassiveState.status === Status.Loading}
        cancelButton={{
          action: () => confirmModal.closeModal(),
          title: 'Cancelar'
        }}
        successButton={{
          action: handleSave,
          title: 'Confirmar'
        }}
      >
        <Typography variant='body2' component={'h4'}>
          Se{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {operationType === 'REMOVE' ? 'Eliminará' : 'Asociará'}
          </Box>
          {' '}la regla VAS{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {selectedVasName}
          </Box>
          {' '}a los productos de la marca{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {selectedOriginName}
          </Box>
          . ¿Desea continuar?
        </Typography>
      </Modal>
    </Fragment>
  )
}
